import { Controller } from 'react-hook-form';
import { TextField, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Autocomplete, Tooltip } from '@efexteam/components';
import {
  CountryCodes,
  EFEX_NAME,
  LOCAL_LABEL,
  SWIFT_LABEL,
} from '@efex-components/common/Form/config';
import { useTranslation } from 'react-i18next';
import useCountryAutocomplete, {
  CountryOption,
  CountryValue,
} from './hooks/useCountryAutocomplete';
import { CountryAutocompleteProps } from './CountryAutocomplete.types';
import countryAutocompleteStyles from './CountryAutocomplete.styles';

const CountryAutocomplete = ({
  name,
  control,
  errors,
  formState,
  defaultValue,
  initialCountry,
  onCountryChange,
  className = countryAutocompleteStyles.defaultContainer,
  label,
  rules,
}: CountryAutocompleteProps) => {
  const { t } = useTranslation();

  const handleCountrySelection = (country: CountryValue | undefined) => {
    if (country) {
      const isEfexSupportedCountry =
        typeof country === 'number' || country === CountryCodes.USA;
      onCountryChange?.(country, isEfexSupportedCountry);
    } else {
      onCountryChange?.('', false);
    }
  };

  const {
    selectedCountryOption,
    autocompleteOptions,
    handleAutocompleteChange,
    getTooltipMessage,
  } = useCountryAutocomplete({
    initialCountry,
    onCountryChange: handleCountrySelection,
  });

  const renderHeaderOption = (option: CountryOption) => (
    <li
      key={option.id}
      className={countryAutocompleteStyles.headerOption}
      onClick={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
        }
      }}
      role="presentation"
      tabIndex={-1}
    >
      <div className={countryAutocompleteStyles.headerContent}>
        <Typography
          variant="subtitle2"
          className={countryAutocompleteStyles.headerLabel}
        >
          {option.label}
        </Typography>
        <Tooltip placement="top" title={getTooltipMessage(option.group)}>
          <InfoIcon
            fontSize="inherit"
            className={countryAutocompleteStyles.infoIcon}
          />
        </Tooltip>
      </div>
    </li>
  );

  const renderRegularOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: CountryOption
  ) => {
    const getGroupLabel = (group: string) =>
      group === EFEX_NAME ? LOCAL_LABEL : SWIFT_LABEL;

    return (
      <li
        {...props}
        key={option.id}
        className={countryAutocompleteStyles.regularOption}
      >
        <div className={countryAutocompleteStyles.optionContent}>
          <Typography variant="body2">{option.label}</Typography>
          <Typography
            variant="caption"
            className={countryAutocompleteStyles.groupLabel}
          >
            {getGroupLabel(option.group)}
          </Typography>
        </div>
      </li>
    );
  };

  const shouldShowError =
    errors[name] &&
    (formState?.isSubmitted || formState?.touchedFields?.[name]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field: { onChange } }) => (
        <Autocomplete
          options={autocompleteOptions}
          value={selectedCountryOption}
          getOptionLabel={(option: CountryOption) =>
            option.isHeader ? '' : option.label
          }
          getOptionDisabled={(option: CountryOption) => !!option.isHeader}
          isOptionEqualToValue={(option: CountryOption, value: CountryOption) =>
            option.id === value.id && !option.isHeader
          }
          onChange={(event, value) => {
            handleAutocompleteChange(event, value);
            onChange(value?.value || '');
          }}
          renderOption={(props, option) => {
            if (option.isHeader) {
              return renderHeaderOption(option);
            }
            return renderRegularOption(props, option);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label || t('ccCountry')}
              error={shouldShowError}
              helperText={shouldShowError ? t('defaultError') : ''}
            />
          )}
          className={className}
          ListboxProps={{
            className: countryAutocompleteStyles.listboxContainer,
          }}
        />
      )}
    />
  );
};

export default CountryAutocomplete;
